import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Search, LogIn, UserPlus, LogOut, Shield, User as UserIcon, Crown, MessageCircle, Gavel } from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function useSiteName() {
  return useMemo(() => {
    try {
      const host = window.location.hostname || "";
      const parts = host.split(".");
      // use 2nd-level domain (e.g. "example" from "forum.example.com")
      let root = parts.length >= 3 ? parts[parts.length - 2] : parts[0];
      if (!root || /^(www|app|forum|preview)$/i.test(root)) {
        root = parts[0] || "Forum";
      }
      return root.charAt(0).toUpperCase() + root.slice(1);
    } catch {
      return "Forum";
    }
  }, []);
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const siteName = useSiteName();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-40 brutal-border border-x-0 border-t-0"
      style={{ background: "var(--bg)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
          <div className="w-10 h-10 brutal-border rounded-xl grid place-items-center brutal-shadow-sm"
            style={{ background: "var(--accent)" }}>
            <MessageCircle size={20} strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl tracking-tight hidden sm:block">
            {siteName.toUpperCase()}
          </span>
        </Link>

        <form onSubmit={submitSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
            <Input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the forum..."
              className="pl-9 h-10 brutal-border rounded-xl bg-white font-medium focus-visible:ring-0 focus-visible:border-[var(--ink)]"
            />
          </div>
        </form>

        <div className="flex items-center gap-2">
          <Link to="/membership" data-testid="nav-membership-link" className="hidden md:block">
            <button className="sticker brutal-hoverable" style={{ background: "var(--pink)" }}>
              <Crown size={14} strokeWidth={2.5} /> Membership
            </button>
          </Link>

          {user ? (
            <>
              {user.role === "moderator" && (
                <span className="sticker hidden sm:inline-flex" style={{ background: "var(--green)" }}>
                  <Gavel size={12} strokeWidth={2.5} /> Mod
                </span>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-link">
                  <Button className="h-10 brutal-border rounded-xl brutal-shadow-sm font-bold gap-2"
                    style={{ background: "var(--secondary)", color: "var(--ink)" }}>
                    <Shield size={16} /> <span className="hidden sm:inline">Admin</span>
                  </Button>
                </Link>
              )}
              <Link to={`/profile/${user.id}`} data-testid="nav-profile-link">
                <Button variant="ghost"
                  className="h-10 brutal-border rounded-xl brutal-shadow-sm font-bold gap-2 bg-white hover:bg-white">
                  <UserIcon size={16} /> <span className="hidden sm:inline max-w-[80px] truncate">{user.name}</span>
                </Button>
              </Link>
              <Button onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout-btn"
                className="h-10 brutal-border rounded-xl brutal-shadow-sm font-bold gap-2"
                style={{ background: "var(--destructive)", color: "white" }}>
                <LogOut size={16} />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link">
                <Button className="h-10 brutal-border rounded-xl brutal-shadow-sm font-bold gap-2 bg-white hover:bg-white text-[var(--ink)]">
                  <LogIn size={16} /> <span className="hidden sm:inline">Log in</span>
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button className="h-10 brutal-border rounded-xl brutal-shadow-sm font-bold gap-2"
                  style={{ background: "var(--primary)", color: "var(--ink)" }}>
                  <UserPlus size={16} /> <span className="hidden sm:inline">Join</span>
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
